/**
 * Reporte de Trazabilidad — versión HTML imprimible.
 *
 * Abrimos una ventana nueva con HTML + CSS corporativo (logo EVEREX navy
 * #003057, tabla con header celeste #EBF5FB) y el usuario imprime / guarda
 * como PDF desde el navegador.
 *
 * Ventaja vs jsPDF: layout 100% fiel al diseño, sin pelear con cursores de mm
 * y tipografías limitadas.
 */

import type { TrazaJobDetalle } from '@/api/contracts/traza-job-detalle';
import type { ComprobanteDetalle } from '@/api/contracts/comprobantes';
import { LOGO_EVEREX_DATA_URI } from '@/assets/logo-everex';

interface GenerarInput {
  traza: TrazaJobDetalle;
  comprobante: ComprobanteDetalle;
  /** Nombre del usuario logueado que genera/imprime el PDF ahora (no es el que
   *  trazó — eso es `traza.usuario`). Se muestra en el pie: "…generado … por X". */
  impresoPor?: string | null;
}

const TIPO_LABEL: Record<string, string> = {
  remito_venta: 'Despacho · Remito de venta',
  transferencia: 'Despacho · Transferencia',
  destruccion: 'Despacho · Destrucción',
};

const STATUS_LABEL: Record<TrazaJobDetalle['status'], string> = {
  pending: 'pendiente',
  running: 'en proceso',
  success: 'informada',
  error: 'con error',
  failed: 'fallido',
  anulada: 'anulada',
};

/**
 * Marca de agua "DOCUMENTO DE PRUEBA" en entornos NO productivos.
 * Señales de no-prod en el front:
 *   - `import.meta.env.DEV` → build de desarrollo (local).
 *   - `VITE_ENV_COLOR` seteada → la misma env var que pinta el header en QA.
 * En producción no aplica ninguna → el reporte sale sin marca.
 */
const MARCA_PRUEBA =
  import.meta.env.DEV ||
  Boolean((import.meta.env.VITE_ENV_COLOR as string | undefined)?.trim());

/** Empresa que firma el reporte — placeholder hasta que tengamos catálogo real. */
const EMPRESA = {
  razonSocial: 'Everex Argentina S.A.',
  cuit: '30-52129849-5',
  direccion: 'García de Cossio 6160, Capital Federal',
  habilitacionAnmat: '—',
};

/** Escapa caracteres especiales para evitar HTML injection desde data ACSIS/riga. */
function esc(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** "2026-06-05T19:01:42.000Z" → "05/06/2026 16:01" (local). */
function fmtFechaHora(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

/** "2026-05-18 00:00:00.000" → "18/05/2026". */
function fmtFechaAcsis(value: string | null | undefined): string {
  if (!value) return '—';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : String(value);
}

/**
 * Construye un mapa serie → codigoTransaccion ANMAT, leyendo el result_payload.
 * Cada chunk tiene su `codigoTransaccion` + un array de `items` con sus series.
 *
 * Se descartan chunks con `error`: si la transacción falló, el código que
 * venga es espúreo (no representa una transacción válida ante ANMAT) —
 * mismo criterio que en `traza_job_detail.anmat_transaction_id` y en la
 * tabla de transacciones del detalle.
 */
function buildSerieToCodigoMap(traza: TrazaJobDetalle): Map<string, string> {
  const map = new Map<string, string>();
  const tx = traza.resultPayload?.transacciones ?? [];
  for (const t of tx) {
    if (!t?.codigoTransaccion) continue;
    if (typeof t.error === 'string' && t.error.length > 0) continue;
    for (const item of t.items ?? []) {
      if (item?.numero_serial) map.set(item.numero_serial, t.codigoTransaccion);
    }
  }
  return map;
}

interface SerieTrazada {
  gtin: string | null;
  sku: string | null;
  lote: string | null;
  serie: string | null;
  /** `DD/MM/YYYY`. */
  vencimiento: string;
}

/** Texto de un campo del payload, o null. */
function campo(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** GTIN a 14 dígitos para comparar el del payload con el del maestro. */
function gtin14(v: string | null | undefined): string {
  const d = (v ?? '').replace(/\D/g, '');
  return d ? d.padStart(14, '0') : '';
}

/**
 * Series que se informaron al SNT en este job. Salen de `requestPayload.articulos`
 * (lo que se le mandó a riga); el SKU se busca por GTIN en el remito. Si el
 * job no tiene payload (trazas viejas), se usan las partidas con serie.
 */
function seriesTrazadas(traza: TrazaJobDetalle, comprobante: ComprobanteDetalle): SerieTrazada[] {
  const articulos = traza.requestPayload?.articulos;
  if (Array.isArray(articulos) && articulos.length > 0) {
    const skuPorGtin = new Map<string, string>();
    for (const x of [...comprobante.items, ...comprobante.partidas]) {
      const g = gtin14(x.gtin);
      if (g && x.sku && !skuPorGtin.has(g)) skuPorGtin.set(g, x.sku);
    }
    return articulos
      .filter((a): a is Record<string, unknown> => typeof a === 'object' && a !== null)
      .map((a) => {
        const gtin = campo(a, 'gtin');
        return {
          gtin,
          sku: skuPorGtin.get(gtin14(gtin)) ?? null,
          lote: campo(a, 'lote'),
          serie: campo(a, 'numero_serial'),
          vencimiento: campo(a, 'vencimiento') ?? '—',
        };
      });
  }
  return comprobante.partidas
    .filter((p) => p.numeroDeSerie)
    .map((p) => ({
      gtin: p.gtin ?? null,
      sku: p.sku,
      lote: p.lote,
      serie: p.numeroDeSerie,
      vencimiento: fmtFechaAcsis(p.fechaVencimiento),
    }));
}

function buildHtml({ traza, comprobante, impresoPor }: GenerarInput): string {
  const tipoLabel =
    (traza.tipoComprobante && TIPO_LABEL[traza.tipoComprobante]) ?? 'Trazabilidad';
  const codigosUnicos = Array.from(new Set(traza.codigosTransaccion));
  const serieToCodigo = buildSerieToCodigoMap(traza);

  // Filas de series: lo que efectivamente se informó al SNT (artículos del
  // request_payload). Cubre los remitos por lote, donde las series salen del
  // pickeo y no de las partidas. Jobs sin payload: partidas con serie.
  const series = seriesTrazadas(traza, comprobante);
  const filasSeries =
    series.length === 0
      ? `<tr><td colspan="6" style="text-align:center;color:#999;padding:14px">Sin series</td></tr>`
      : series
          .map((x) => {
            const codigo = x.serie ? (serieToCodigo.get(x.serie) ?? '—') : '—';
            return `
        <tr>
          <td class="mono">${esc(x.gtin ?? '—')}</td>
          <td class="mono">${esc(x.sku)}</td>
          <td>${esc(x.lote)}</td>
          <td class="mono">${esc(x.serie)}</td>
          <td>${esc(x.vencimiento)}</td>
          <td class="mono small">${esc(codigo)}</td>
        </tr>`;
          })
          .join('');

  // GLN destino (del request_payload que mandamos a riga)
  const glnDestino = (() => {
    const v = traza.requestPayload?.gln_destino;
    return typeof v === 'string' ? v : '—';
  })();

  // Evento ANMAT: código + descripción. Preferimos el snapshot persistido en el
  // job (event_code / event_desc); si es un job viejo sin esas columnas, caemos
  // al id_evento del request_payload (solo el código).
  const eventoAnmat = (() => {
    const payloadCode = traza.requestPayload?.id_evento;
    const code =
      traza.eventCode ??
      (typeof payloadCode === 'string' || typeof payloadCode === 'number'
        ? payloadCode
        : null);
    if (code == null || code === '') return '—';
    return traza.eventDesc ? `${code} — ${traza.eventDesc}` : String(code);
  })();

  const codigosHtml =
    codigosUnicos.length === 0
      ? '—'
      : codigosUnicos.map((c) => `<span class="chip-codigo">${esc(c)}</span>`).join(' ');

  // N° de remito para mostrar en el encabezado (arriba a la derecha, debajo del
  // paginador). Preferimos el número AFIP legible (R-0001-…); si no, el remito
  // enviado o la referencia.
  const remito =
    comprobante.numeroAfip ?? traza.numeroRemito ?? comprobante.referencia ?? '—';

  return `<!DOCTYPE html>
<html lang="es"><head>
<meta charset="UTF-8">
<title>Reporte de Trazabilidad — ${esc(comprobante.referencia)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&family=Roboto+Mono:wght@400;500&display=swap" rel="stylesheet">
<style>
  * { box-sizing: border-box; }
  body {
    font-family: 'Roboto', -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
    margin: 0;
    padding: 30px 40px;
    color: #1B2A4A;
    font-size: 11pt;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  /* Paginado por JS: el reporte se corta en "hojas" A4 (.sheet), cada una con
     su ENCABEZADO completo (.page-header) + su tramo de filas + su PIE completo
     (.page-footer). Ancho fijo = área imprimible A4 (210 − 2×12 mm) para que el
     texto envuelva igual en pantalla y en impresión. Ver el <script> al final. */
  #doc { width: 186mm; margin: 0 auto; }
  .sheet { width: 186mm; margin: 0 auto; position: relative; }
  .sheet + .sheet { break-before: page; }
  /* Bloques del reporte. display:flow-root CONTIENE los márgenes de los hijos
     (evita el "margin collapsing"), así la medición del paginado
     (getBoundingClientRect de cada bloque) es exacta y las hojas no se
     desbordan. header/footer se repiten en cada hoja; intro va solo en la 1ª
     página y estado solo en la última. */
  .page-header, .intro, .estado, .page-footer { display: flow-root; }
  /* Meta arriba a la derecha de cada hoja: "Página X de Y" y, debajo, el N° de
     remito (más grande). Absolute para no afectar la altura que mide el paginado. */
  .page-meta {
    position: absolute;
    top: 0;
    right: 0;
    text-align: right;
  }
  .page-num { font-size: 8pt; font-weight: 500; color: #888; }
  .page-remito {
    margin-top: 1px;
    font-family: 'Roboto Mono', ui-monospace, "SF Mono", Consolas, monospace;
    font-size: 13pt;
    font-weight: 700;
    color: #1B2A4A;
  }
  /* Marca de agua de entornos NO productivos: diagonal, sutil pero visible, sin
     bloquear la lectura (baja opacidad + pointer-events:none). Una por hoja. */
  .watermark {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-32deg);
    font-size: 50pt;
    font-weight: 800;
    letter-spacing: 6px;
    white-space: nowrap;
    color: rgba(190, 30, 30, .09);
    pointer-events: none;
    z-index: 0;
  }

  .header {
    border-bottom: 3px solid #1B2A4A;
    padding-bottom: 16px;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .logo {
    height: 48px;
    width: auto;
    flex-shrink: 0;
    transform: translateY(-6px);
  }
  .empresa { flex: 1; }
  .empresa h1 { margin: 0; font-size: 12pt; color: #003057; }
  .empresa .datos { font-size: 9pt; color: #555; line-height: 1.4; margin-top: 4px; }

  .titulo-reporte {
    text-align: center;
    font-size: 16pt;
    font-weight: bold;
    margin: 20px 0 24px;
    color: #1B2A4A;
  }

  .grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 24px;
  }
  .field-label {
    font-size: 8pt;
    text-transform: uppercase;
    color: #888;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    font-weight: bold;
  }
  .field-row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
    font-size: 10pt;
    border-bottom: 1px dotted #e0e0e0;
  }
  .field-row span { color: #666; }
  .field-row b { color: #1B2A4A; text-align: right; }
  .field-row b.mono { font-family: 'Roboto Mono', ui-monospace, "SF Mono", Consolas, monospace; font-size: 9.5pt; }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
    font-size: 9pt;
  }
  th {
    background: #EBF5FB;
    color: #1B2A4A;
    text-align: left;
    padding: 5px 6px;
    border-bottom: 2px solid #1B2A4A;
    font-size: 8pt;
    text-transform: uppercase;
  }
  td {
    padding: 3px 6px;
    border-bottom: 1px solid #E5E7E9;
    vertical-align: top;
    line-height: 1.2;
  }
  td.num { text-align: right; }
  .mono {
    font-family: 'Roboto Mono', ui-monospace, "SF Mono", Consolas, monospace;
    font-size: 8.5pt;
  }
  .small { font-size: 8pt; }

  .section-title {
    font-size: 11pt;
    font-weight: bold;
    margin: 20px 0 8px;
    color: #1B2A4A;
    border-bottom: 1px solid #AED6F1;
    padding-bottom: 4px;
  }

  .chip-codigo {
    display: inline-block;
    background: #EBF5FB;
    color: #1B2A4A;
    padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Roboto Mono', ui-monospace, "SF Mono", Consolas, monospace;
    font-size: 9pt;
    margin-right: 4px;
  }

  .estado-badge {
    display: inline-block;
    padding: 3px 12px;
    border-radius: 4px;
    font-weight: bold;
    text-transform: uppercase;
    font-size: 9pt;
    letter-spacing: 0.5px;
  }
  .estado-success { background: #D4EFDF; color: #196F3D; }
  .estado-error   { background: #FADBD8; color: #922B21; }
  .estado-warn    { background: #FCF3CF; color: #7D6608; }

  .footer {
    margin-top: 30px;
    font-size: 9pt;
    color: #666;
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #AED6F1;
    padding-top: 12px;
  }

  /* Botón SOLO de pantalla (display:none al imprimir). Va abajo a la derecha
     para no taparse con el "Página X de Y" del encabezado. No afecta el PDF. */
  .print-btn {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 1000; /* por encima de las hojas (position:relative), sino lo tapan */
    background: #E67E22;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-weight: bold;
    cursor: pointer;
    font-size: 11pt;
    box-shadow: 0 2px 8px rgba(0,0,0,.15);
  }
  .print-btn:hover { background: #1B2A4A; }

  /* SOLO PANTALLA — no afecta el PDF (ahí manda @media print). Cada hoja se ve
     como una página A4 real: 210×297mm con 12mm de margen interno (igual que el
     @page del PDF), así el contenido no queda pegado a los bordes. Uso
     content-box para que el ancho ÚTIL siga siendo 186mm (el que mide el
     paginado) y el padding sume por fuera hasta los 210mm. */
  @media screen {
    body { background: #e5e7eb; padding: 24px 16px; }
    .sheet {
      box-sizing: content-box;
      width: 186mm;
      padding: 12mm;
      min-height: 273mm;
      background: #fff;
      box-shadow: 0 2px 10px rgba(0, 0, 0, .18);
      border-radius: 2px;
      margin: 0 auto 20px;
    }
    /* En pantalla la hoja tiene 12mm de padding, así que corremos el bloque
       (paginador + remito) 12mm para que quede alineado con el contenido y no
       pegado al borde del papel. En impresión no hay padding → sigue en right:0
       (borde del contenido), así que el PDF NO cambia. */
    .page-meta { top: 12mm; right: 12mm; }
  }

  @media print {
    .print-btn { display: none; }
    body { padding: 0; background: #fff; }
    @page { size: A4; margin: 12mm; }
    /* Cada hoja ocupa el ancho imprimible; el salto lo da .sheet + .sheet. */
    #doc, .sheet { width: auto; margin: 0; }
    tr { break-inside: avoid; }
  }
</style>
</head><body>
<button class="print-btn" onclick="window.print()">Imprimir / Guardar PDF</button>

<!-- #doc: header + intro(pág.1) + tabla + estado(últ.pág) + footer. El <script>
     lo corta en hojas: HEADER y FOOTER se REPITEN en cada una; la INTRO va solo
     en la 1ª página y ESTADO ANMAT solo en la última. Si el JS no corre, se
     imprime este flujo tal cual (todo una vez): degradación aceptable. -->
<div id="doc">
  <!-- HEADER (se repite en cada hoja): logo + empresa. El "Página X de Y" lo
       agrega el script arriba a la derecha de cada hoja. -->
  <div class="page-header">
    <div class="header">
      <img class="logo" src="${LOGO_EVEREX_DATA_URI}" alt="EVEREX Pharmaceuticals">
      <div class="empresa">
        <h1>${esc(EMPRESA.razonSocial)}</h1>
        <div class="datos">
          ${esc(EMPRESA.direccion)}<br>
          CUIT: ${esc(EMPRESA.cuit)}
        </div>
      </div>
    </div>
  </div>

  <!-- INTRO (solo página 1): título + datos de la operación / emisor / destino -->
  <div class="intro">
    <div class="titulo-reporte">REPORTE DE TRAZABILIDAD</div>

    <div class="grid2">
      <div>
        <div class="field-label">Datos de la operación</div>
        <div class="field-row"><span>Job riga-traza:</span><b class="mono">#${esc(traza.jobId)}</b></div>
        <div class="field-row"><span>Referencia ERP:</span><b class="mono">${esc(comprobante.referencia)}</b></div>
        <div class="field-row"><span>Tipo:</span><b>${esc(tipoLabel)}</b></div>
        <div class="field-row"><span>Comprobante:</span><b class="mono">${esc(comprobante.numeroAfip ?? '—')}</b></div>
        <div class="field-row"><span>N° Remito enviado:</span><b class="mono">${esc(traza.numeroRemito ?? '—')}</b></div>
        <div class="field-row"><span>Fecha trazado:</span><b>${esc(fmtFechaHora(traza.finishedAt ?? traza.createdAt))}</b></div>
        <div class="field-row"><span>Operador:</span><b>${esc(traza.usuario.nombre)}</b></div>
      </div>

      <div>
        <div class="field-label">Emisor (origen)</div>
        <div class="field-row"><span>Razón social:</span><b>${esc(traza.gln.nombre)}</b></div>
        <div class="field-row"><span>GLN:</span><b class="mono">${esc(traza.gln.gln)}</b></div>

        <div class="field-label" style="margin-top:14px">Destino</div>
        <div class="field-row"><span>Cliente ID (ERP):</span><b class="mono">${esc(comprobante.comprobante.clienteId ?? '—')}</b></div>
        <div class="field-row"><span>GLN destino:</span><b class="mono">${esc(glnDestino)}</b></div>
        <div class="field-row"><span>Evento ANMAT:</span><b>${esc(eventoAnmat)}</b></div>
      </div>
    </div>

    <div class="section-title">SERIES TRAZADAS (${series.length})</div>
  </div>

  <table class="report">
    <thead>
      <tr>
        <th>GTIN</th>
        <th>SKU</th>
        <th>Lote</th>
        <th>N° Serie</th>
        <th>Vencimiento</th>
        <th>Código transacción</th>
      </tr>
    </thead>
    <tbody>${filasSeries}</tbody>
  </table>

  <!-- ESTADO ANMAT (solo última página) -->
  <div class="estado">
    <div class="section-title">ESTADO ANMAT</div>
    <div class="field-row">
      <span>Estado:</span>
      <b>
        <span class="estado-badge ${
          traza.status === 'success'
            ? 'estado-success'
            : traza.status === 'error' || traza.status === 'failed'
              ? 'estado-error'
              : 'estado-warn'
        }">${esc(STATUS_LABEL[traza.status])}</span>
      </b>
    </div>
    <div class="field-row"><span>Códigos de transacción:</span><b>${codigosHtml}</b></div>
    <div class="field-row"><span>Items:</span><b>${esc(traza.doneItems ?? 0)} OK${
      traza.errorItems && traza.errorItems > 0 ? ` · ${esc(traza.errorItems)} con error` : ''
    }${traza.totalItems != null ? ` / ${esc(traza.totalItems)} totales` : ''}</b></div>
    ${traza.errorMessage ? `<div class="field-row"><span>Error sistémico:</span><b style="color:#922B21">${esc(traza.errorMessage)}</b></div>` : ''}
  </div>

  <!-- FOOTER (se repite en cada hoja): línea "Reporte generado el … por …" -->
  <div class="page-footer">
    <div class="footer">
      <div>Reporte generado el ${esc(fmtFechaHora(new Date().toISOString()))}${impresoPor ? ` por ${esc(impresoPor)}` : ''}</div>
      <div>${esc(EMPRESA.razonSocial)} — Sistema de Trazabilidad</div>
    </div>
  </div>
</div>

<script>
  /* Paginado WYSIWYG: cortamos las filas en hojas A4. Cada hoja lleva el header
     y el footer COMPLETOS (clonados) + su tramo de filas. Medimos las alturas
     reales (a ancho A4, porque #doc mide 186mm también en pantalla) para saber
     cuántas filas entran por hoja. Lo que se ve en pantalla = lo que se imprime. */
  (function () {
    var REMITO = ${JSON.stringify(remito)};
    var MARCA = ${MARCA_PRUEBA};
    function px(mm) { return mm / 25.4 * 96; }
    function paginar() {
      var doc = document.getElementById('doc');
      if (!doc || doc.getAttribute('data-paginado')) return;
      var header = doc.querySelector('.page-header');
      var footer = doc.querySelector('.page-footer');
      var intro = doc.querySelector('.intro');
      var estado = doc.querySelector('.estado');
      var table = doc.querySelector('table.report');
      var thead = table.querySelector('thead');
      var filas = Array.prototype.slice.call(table.querySelectorAll('tbody > tr'));

      var ALTO = px(297 - 24); // área imprimible A4 (297 − 2×12 mm)
      var COLCHON = 16;
      var H = header.getBoundingClientRect().height;   // se repite en cada hoja
      var F = footer.getBoundingClientRect().height;   // se repite en cada hoja
      var C = thead.getBoundingClientRect().height;    // cabecera de columnas
      var R = filas.length ? filas[0].getBoundingClientRect().height : 20;
      var INTRO = intro.getBoundingClientRect().height;   // solo 1ª página
      var ESTADO = estado.getBoundingClientRect().height; // solo última página

      // Filas que entran según los bloques extra que tenga la hoja.
      function cap(extra) {
        return Math.max(1, Math.floor((ALTO - H - F - C - COLCHON - extra) / R));
      }
      var capFirst = cap(INTRO);          // 1ª (no única): resta la intro
      var capMid = cap(0);                // intermedias: solo header/footer/cols
      var capLast = cap(ESTADO);          // última: resta el estado
      var capOnly = cap(INTRO + ESTADO);  // única hoja (1ª y última a la vez)

      // Reparto de filas en páginas, marcando cuál lleva la intro (1ª) y cuál
      // el estado (última).
      var pages = [];
      var n = filas.length, idx = 0;
      if (n === 0) {
        pages.push({ start: 0, count: 0, first: true, last: true });
      } else {
        while (idx < n) {
          var first = (pages.length === 0);
          var rem = n - idx;
          var capNotLast = first ? capFirst : capMid;
          var capAsLast = first ? capOnly : capLast;
          if (rem <= capAsLast) {
            pages.push({ start: idx, count: rem, first: first, last: true });
            idx = n;
          } else if (rem <= capNotLast) {
            // Entran todas sin el estado, pero con el estado no → hoja extra
            // final que lleva SOLO el ESTADO ANMAT.
            pages.push({ start: idx, count: rem, first: first, last: false });
            pages.push({ start: n, count: 0, first: false, last: true });
            idx = n;
          } else {
            pages.push({ start: idx, count: capNotLast, first: first, last: false });
            idx += capNotLast;
          }
        }
      }

      var total = pages.length;
      var frag = document.createDocumentFragment();
      for (var p = 0; p < pages.length; p++) {
        var pg = pages[p];
        var hoja = document.createElement('div');
        hoja.className = 'sheet';
        if (MARCA) {
          var wm = document.createElement('div');
          wm.className = 'watermark';
          wm.textContent = 'DOCUMENTO DE PRUEBA';
          hoja.appendChild(wm);
        }
        var meta = document.createElement('div');
        meta.className = 'page-meta';
        var num = document.createElement('div');
        num.className = 'page-num';
        num.textContent = 'Página ' + (p + 1) + ' de ' + total;
        meta.appendChild(num);
        var rem = document.createElement('div');
        rem.className = 'page-remito';
        rem.textContent = REMITO;
        meta.appendChild(rem);
        hoja.appendChild(meta);
        hoja.appendChild(header.cloneNode(true));
        if (pg.first) hoja.appendChild(intro.cloneNode(true));
        if (pg.count > 0) {
          var t = document.createElement('table');
          t.className = 'report';
          t.appendChild(thead.cloneNode(true));
          var tb = document.createElement('tbody');
          for (var j = 0; j < pg.count; j++) tb.appendChild(filas[pg.start + j].cloneNode(true));
          t.appendChild(tb);
          hoja.appendChild(t);
        }
        if (pg.last) hoja.appendChild(estado.cloneNode(true));
        hoja.appendChild(footer.cloneNode(true));
        frag.appendChild(hoja);
      }

      // Ya medimos con #doc a 186mm: ahora lo liberamos para que las "páginas"
      // (más anchas por el margen interno en pantalla) se centren. En impresión
      // #doc ya es width:auto (@media print), así que esto NO cambia el layout.
      doc.style.width = 'auto';
      doc.innerHTML = '';
      doc.appendChild(frag);
      doc.setAttribute('data-paginado', '1');
    }
    // Paginar cuando el layout está listo. IMPORTANTE: esperamos a que la fuente
    // (Roboto) termine de cargar con document.fonts.ready — sino mediríamos con
    // la fuente de fallback y las hojas cortarían mal. Si la fuente falla (offline),
    // fonts.ready igual resuelve y paginamos con el fallback. El 'load' cubre la
    // ventana del reporte (document.write) tras document.close.
    function arrancar() {
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(paginar);
      else paginar();
    }
    if (document.readyState === 'complete') arrancar();
    else window.addEventListener('load', arrancar);
  })();
</script>
</body></html>`;
}

/**
 * Abre el reporte en una ventana nueva. El usuario imprime / guarda como PDF
 * desde el navegador (botón naranja arriba a la derecha + atajo Cmd/Ctrl+P).
 *
 * Si el navegador bloquea el pop-up, devuelve falso y el caller debe avisar
 * al usuario para que permita pop-ups.
 */
export function abrirReporteTrazabilidad(input: GenerarInput): boolean {
  const html = buildHtml(input);
  const w = window.open('', '_blank', 'width=1000,height=900');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

/**
 * Alias retrocompatible — los lugares que importaban `generarPdfTrazaJob`
 * siguen funcionando. Devuelve Promise para no cambiar la integración del caller.
 */
export function generarPdfTrazaJob(input: GenerarInput): Promise<void> {
  return Promise.resolve().then(() => {
    const ok = abrirReporteTrazabilidad(input);
    if (!ok) {
      throw new Error('El navegador bloqueó la ventana del reporte. Permití pop-ups y reintentá.');
    }
  });
}
